import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiClock, FiFileText, FiChevronRight, FiUser, FiHome } from 'react-icons/fi';

export function Onboard() {
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if returning from a completed step
  useEffect(() => {
    // If there's state passed from the previous page
    if (location.state && location.state.completedStep) {
      setCurrentStep(location.state.completedStep + 1);
    }
  }, [location]);
  
  if (!currentUser) {
    // Redirect to login if not authenticated
    return <div className="p-8 text-center">
      <h2 className="text-2xl font-bold mb-4">Please log in first</h2>
      <Link to="/" className="text-blue-600 hover:underline">Go to login</Link>
    </div>;
  }
  
  // Functions to handle navigation between steps
  const handleSetupSecretary = () => {
    // Navigate to the secretary setup page
    navigate('/setup-secretary');
  };
  
  const handleConfigureSchedule = () => {
    // Navigate to the main app which has the schedule configuration
    navigate('/');
  };
  
  const handleImportDocs = () => {
    // In a real app, this would open the docs import interface
    setCurrentStep(3);
    alert("Document import interface would open here");
  };
  
  const handleViewDocs = () => {
    window.open('https://secrely.ai/docs', '_blank');
  };
  
  const handleContactSupport = () => {
    window.open('mailto:support@secrely.ai', '_blank');
  };
  
  // Calculate progress based on current step
  const progressPercentage = Math.min(currentStep * 25, 100);
  
  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Welcome to Secrely</h1>
          <Button size="sm" variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
            <FiHome size={14} />
            Return to App
          </Button>
        </div>
        
        {/* Progress indicator */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-sm text-gray-500">Setting Up</div>
            <div className="text-sm font-medium">{currentStep}/4</div>
          </div>
          <div className="h-1.5 w-full bg-gray-200 rounded-full">
            <div 
              className="h-full bg-blue-500 rounded-full transition-all duration-300" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
        
        <h2 className="text-xl font-semibold mb-4">Your AI Secretary</h2>
        
        {/* Secretary setup card */}
        <div className="mb-4 border rounded-md overflow-hidden shadow-sm">
          <div className={`flex items-center p-4 ${currentStep >= 1 ? 'bg-blue-50' : 'bg-white'}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center mr-4 ${currentStep >= 1 ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <FiUser className={currentStep >= 1 ? 'text-blue-600' : 'text-gray-500'} size={18} />
            </div>
            <div className="flex-1">
              <h3 className={`text-base font-medium ${currentStep >= 1 ? 'text-blue-900' : 'text-gray-800'}`}>Set up your secretary</h3>
              <p className={`text-xs ${currentStep >= 1 ? 'text-blue-700' : 'text-gray-500'}`}>Personalize the identity</p>
            </div>
            <Button 
              className="ml-auto"
              variant={currentStep >= 1 ? "default" : "outline"}
              size="sm"
              onClick={handleSetupSecretary}
            >
              {currentStep > 1 ? 'Completed' : 'Start'}
            </Button>
          </div>
        </div>
        
        {/* Configure schedule card */}
        <div className="mb-4 border rounded-md overflow-hidden shadow-sm">
          <div className={`flex items-center p-4 ${currentStep >= 2 ? 'bg-blue-50' : 'bg-white'}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center mr-4 ${currentStep >= 2 ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <FiClock className={currentStep >= 2 ? 'text-blue-600' : 'text-gray-500'} size={18} />
            </div>
            <div className="flex-1">
              <h3 className={`text-base font-medium ${currentStep >= 2 ? 'text-blue-900' : 'text-gray-800'}`}>Configure your schedule</h3>
              <p className={`text-xs ${currentStep >= 2 ? 'text-blue-700' : 'text-gray-500'}`}>Set up your availability</p>
            </div>
            <Button 
              variant={currentStep >= 2 ? "default" : "outline"}
              size="sm"
              className="ml-auto"
              onClick={handleConfigureSchedule}
            >
              {currentStep > 2 ? 'Completed' : 'Configure'}
            </Button>
          </div>
        </div>
        
        {/* Import docs card */}
        <div className="mb-4 border rounded-md overflow-hidden shadow-sm">
          <div className={`flex items-center p-4 ${currentStep >= 3 ? 'bg-blue-50' : 'bg-white'}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center mr-4 ${currentStep >= 3 ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <FiFileText className={currentStep >= 3 ? 'text-blue-600' : 'text-gray-500'} size={18} />
            </div>
            <div className="flex-1">
              <h3 className={`text-base font-medium ${currentStep >= 3 ? 'text-blue-900' : 'text-gray-800'}`}>Import your docs</h3>
              <p className={`text-xs ${currentStep >= 3 ? 'text-blue-700' : 'text-gray-500'}`}>Create your knowledge base</p>
            </div>
            <Button 
              variant={currentStep >= 3 ? "default" : "outline"}
              size="sm"
              className="ml-auto"
              onClick={handleImportDocs}
            >
              {currentStep > 3 ? 'Completed' : 'Import'}
            </Button>
          </div>
        </div>
        
        {/* Support section */}
        <div className="mt-8 bg-gray-50 p-5 rounded-lg border">
          <h3 className="text-base font-semibold mb-2">Need help?</h3>
          <p className="text-sm text-gray-600 mb-4">Our support team is here to help you get started</p>
          
          <div className="space-y-2">
            <div 
              className="flex items-center justify-between p-3 bg-white border rounded-md cursor-pointer hover:bg-gray-50"
              onClick={handleViewDocs}
            >
              <span className="flex items-center text-sm">
                <FiFileText className="mr-2 text-gray-500" size={16} /> View documentation
              </span>
              <FiChevronRight className="text-gray-400" size={18} />
            </div>
            
            <div 
              className="flex items-center justify-between p-3 bg-white border rounded-md cursor-pointer hover:bg-gray-50"
              onClick={handleContactSupport}
            >
              <span className="flex items-center text-sm">
                <FiClock className="mr-2 text-gray-500" size={16} /> Contact support
              </span>
              <FiChevronRight className="text-gray-400" size={18} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 